// Team Users displays the list of users for a Team (usergroup with isTeam property = true)
import React, { useEffect, useState } from "react";
import { Typography } from "@optimizely/axiom";
import { User } from "../../../domain/User";
import { useUsersByGroup } from "../../../hooks/useUsersByGroup/useUsersByGroup";
import { ProgressDots, Table } from "@optimizely/axiom";
import styles from "../UserGroupForm/UserGroupFormUsers/UserGroupFormUsers.module.scss";
import { InfiniteScrollList } from "../InfiniteScrollList/InfiniteScrollList";
import { UserTableRow } from "../UserTableRow/UserTableRow";
import { useUserContext } from "../../../providers/UserProvider";
import { UserSearchSelector } from "../UserSearchSelector/UserSearchSelector";

export const TeamUsers = ({
    disabled = false,
    loading = false,
    editing,
    userGroupId,
    onChange
}: {
    loading?: boolean;
    disabled?: boolean;
    editing?: boolean;
    userGroupId?: string;
    onChange: ({ usersToAdd, usersToRemove }: { usersToAdd: User[]; usersToRemove: User[] }) => void;
}) => {
    const { users, loadNext, isLoadingMore, isLoadingInitialData } = useUsersByGroup({ userGroupId });
    const { organizationId } = useUserContext();
    const [displayedUsers, setDisplayedUsers] = useState<User[]>([]);
    const [usersToAdd, setUsersToAdd] = useState<User[]>([]);
    const [usersToRemove, setUsersToRemove] = useState<User[]>([]);

    useEffect(() => {
        if (users) {
            setDisplayedUsers(users);
        }
    }, [users]);

    useEffect(() => {
        if (usersToAdd?.length > 0 || usersToRemove?.length > 0) {
            onChange({ usersToAdd, usersToRemove });
        }
    }, [onChange, usersToAdd, usersToRemove]);

    useEffect(() => {
        if (!editing && (usersToAdd.length > 0 || usersToRemove.length > 0)) {
            // reset users to add/remove when exiting edit mode
            setUsersToAdd([]);
            setUsersToRemove([]);
        }
    }, [editing, usersToAdd.length, usersToRemove.length]);

    const removeUser = ({ index }: { index: number }) => {
        const updatedUsers = [...displayedUsers];
        const userToRemove = displayedUsers[index];
        updatedUsers.splice(index, 1);
        // remove the user from the users to add if they exist in there
        setUsersToAdd((prev) => {
            return prev.filter((user) => user.email !== userToRemove.email);
        });
        setUsersToRemove((prev) => {
            const nextUsersToRemove = [...prev, userToRemove];
            onChange({ usersToAdd, usersToRemove: nextUsersToRemove });
            return nextUsersToRemove;
        });
        setDisplayedUsers(updatedUsers);
    };

    const addUser = (newUser?: User) => {
        if (newUser) {
            // remove the user from the users to remove if they exist in there
            setUsersToRemove((prev) => {
                return prev.filter((user) => user.email !== newUser.email);
            });
            setUsersToAdd((prev) => {
                const nextUsersToAdd = [...prev, newUser];
                onChange({ usersToAdd: nextUsersToAdd, usersToRemove });
                return nextUsersToAdd;
            });
            setDisplayedUsers((prev) => [newUser, ...prev]);
        }
    };

    const isUserDisabled = (user: User) => {
        return displayedUsers.map((u) => u.email).includes(user.email);
    };

    if (isLoadingInitialData) {
        return <div>Loading...</div>;
    }

    if (!(editing || displayedUsers?.length)) {
        return (
            <Typography type="body" className="label--disabled">
                This Team Has No Users.
            </Typography>
        );
    }

    return (
        <>
            {editing && (
                <UserSearchSelector disabled={disabled} onSelectUser={addUser} isUserDisabled={isUserDisabled} />
            )}
            <Table
                className={`push--top ${styles["user-group-form-users__table"]}`}
                // eslint-disable-next-line react/style-prop-object
                style="rule"
            >
                <Table.TBody>
                    <InfiniteScrollList
                        isLoadingMore={isLoadingMore}
                        loadNext={loadNext}
                        loadingItem={
                            <Table.TR>
                                <Table.TD colSpan={3} textAlign="center">
                                    <ProgressDots />
                                </Table.TD>
                            </Table.TR>
                        }
                    >
                        {displayedUsers?.map((item, itemIndex) => (
                            <UserTableRow
                                showExternal={item.homeOrganizationId !== organizationId}
                                user={item}
                                key={item.email}
                                removeTitle="Remove user from team"
                                onRemove={editing ? () => removeUser({ index: itemIndex }) : undefined}
                            />
                        ))}
                    </InfiniteScrollList>
                </Table.TBody>
            </Table>
        </>
    );
};

TeamUsers.displayName = "TeamUsers";
